import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

import { useSeo } from "@/hooks/useSeo";
import heroLift from "@/assets/hero-lift.webp";
import { SiteLayout, Eyebrow } from "@/components/landing/SiteLayout";
import { Reveal } from "@/components/landing/Reveal";
import { DecorHatch, DecorDiagonal, DecorShaft, DecorFloors } from "@/components/landing/Decor";


import { SoFunktioniertSection } from "@/components/landing/sections/SoFunktioniertSection";
import { LiftartenSection } from "@/components/landing/sections/LiftartenSection";
import { FAQSection } from "@/components/landing/sections/FAQSection";
import { KontaktSection } from "@/components/landing/sections/KontaktSection";
import { ThemenSection } from "@/components/landing/sections/ThemenSection";

// Einheitliche CTA-Geometrie für die gesamte Startseite
const CTA_BASE =
  "h-12 px-7 rounded-full text-[15px] font-medium transition-transform active:scale-[0.99] w-full sm:w-auto";
const CTA_PRIMARY =
  "min-h-[48px] sm:min-h-[52px] px-8 rounded-full text-[15.5px] font-semibold w-full sm:w-auto transition-all duration-200 shadow-[0_10px_30px_-16px_rgba(0,0,0,0.9)] hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-16px_rgba(0,0,0,0.9)] active:translate-y-0 active:scale-[0.99]";


const Landing = () => {
  const navigate = useNavigate();
  const goWizard = () => navigate("/wizard");


  useSeo({
    title: "Liftanbieter Schweiz finden | Liftblick.ch",
    description:
      "Liftanbieter in der Schweiz finden. Projekt kostenlos erfassen, passende Anbieter für Aufzug, Homelift und mehr melden sich direkt.",
    canonical: "https://liftblick.ch/",
  });

  useEffect(() => {
    const ldId = "liftblick-jsonld";
    document.getElementById(ldId)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = ldId;
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Liftblick.ch",
        url: "https://liftblick.ch",
        areaServed: "CH",
        description:
          "Schweizer Plattform für Liftanfragen. Verbindet Kunden mit registrierten Liftanbietern.",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Liftblick.ch",
        url: "https://liftblick.ch",
        inLanguage: "de-CH",
      },
    ]);
    document.head.appendChild(script);
    return () => {
      document.getElementById(ldId)?.remove();
    };
  }, []);

  return (
    <SiteLayout>
      {/* ══ HERO ════════════════════════════════════ */}
      <section className="relative isolate min-h-[max(460px,62svh)] sm:min-h-[max(520px,72svh)] overflow-hidden flex flex-col justify-center">
        <img
          src={heroLift}
          alt="Moderner Personenaufzug mit Glaskabine in einem hellen Treppenhaus in der Schweiz"
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          loading="eager"
          decoding="async"
        />

        <div
          aria-hidden
          className="absolute inset-0 z-0 sm:hidden"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,8,12,0.86) 0%, rgba(6,8,12,0.72) 55%, rgba(6,8,12,0.48) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 z-0 hidden sm:block"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,8,12,0.72) 0%, rgba(6,8,12,0.58) 55%, rgba(6,8,12,0.55) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 z-0 hidden sm:block"
          style={{
            background:
              "radial-gradient(70% 60% at 50% 50%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 75%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 sm:h-24"
          style={{
            background:
              "linear-gradient(180deg, rgba(248,249,250,0) 0%, rgba(248,249,250,0.6) 70%, #F8F9FA 100%)",
          }}
        />

        {/* Grafische Akzente im Hero */}

        <div className="relative z-20 w-full max-w-3xl mx-auto px-5 sm:px-6 py-12 sm:py-16 flex flex-col items-center text-center">

          <h1 className="text-white font-semibold leading-[1.08] sm:leading-[1.05] tracking-[-0.02em] sm:tracking-[-0.03em] text-[clamp(27px,7.6vw,38px)] text-balance [hyphens:none] sm:text-6xl lg:text-7xl xl:text-[76px] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
            <span className="block animate-fade-up">Liftanbieter finden.</span>
            <span className="block animate-fade-up [animation-delay:100ms]">Direkt und regional.</span>
          </h1>

          <p className="animate-fade-up [animation-delay:340ms] mt-4 sm:mt-5 text-white text-[15.5px] min-[400px]:text-[16px] sm:text-[18.5px] lg:text-[19.5px] leading-[1.5] sm:leading-[1.6] max-w-[30ch] sm:max-w-xl text-balance font-medium [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]">
            Erfassen Sie Ihr Liftprojekt kostenlos online <br className="hidden sm:block" />
            und werden Sie direkt kontaktiert von passenden Anbietern.
          </p>


          <div className="animate-fade-up [animation-delay:460ms] mt-7 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-5 w-full sm:w-auto">
            <Button size="lg" onClick={goWizard} className={CTA_PRIMARY}>
              Anfrage starten
            </Button>
            <Link
              to="/#so-funktioniert"
              className="inline-flex h-12 items-center justify-center px-1 text-[15px] font-medium text-white/90 transition-colors hover:text-white sm:border-b sm:border-white/40 sm:hover:border-white [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]"
            >
              So funktioniert es
            </Link>
          </div>

          <p className="animate-fade-up [animation-delay:500ms] mt-4 sm:mt-5 text-[13.5px] text-white/85 [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]">
            Kostenlos - unverbindlich - in rund 3 Minuten erfasst
          </p>

          {/* Direkte Einstiege nach Anliegen (Desktop) */}
          <div className="animate-fade-up [animation-delay:540ms] mt-7 w-full hidden sm:block">
            <p className="text-[13px] uppercase tracking-[0.14em] text-white/65">Direkt starten</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
              {[
                ["Neubau", "Neubau"],
                ["Modernisierung", "Modernisierung"],
                ["Wartung", "Wartung"],
              ].map(([label, value]) => (
                <Link
                  key={value}
                  to={`/wizard?anliegen=${value}`}
                  className="inline-flex h-10 items-center rounded-full border border-white/25 bg-white/10 px-4 text-[14px] font-medium text-white backdrop-blur-[2px] transition-colors hover:bg-white/20 hover:border-white/45"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          <ul className="animate-fade-up [animation-delay:600ms] mt-8 hidden sm:flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[13.5px] !text-white font-medium [text-shadow:0_2px_6px_rgba(0,0,0,0.95),0_1px_2px_rgba(0,0,0,1)]">
            {[
              "Kostenlos und unverbindlich",
              "Aufzüge, Homelifte, Wartung",
              "Regionale Schweizer Anbieter",
            ].map((t) => (
              <li key={t} className="inline-flex items-center gap-2 !text-white whitespace-nowrap">
                <span aria-hidden className="h-px w-3.5 shrink-0 bg-current opacity-60" />
                <span className="!text-white">{t}</span>
              </li>
            ))}
          </ul>
        </div>

      </section>

      {/* ══ DIREKT STARTEN (Mobile) ═════════════════ */}
      <section aria-label="Direkt starten" className="sm:hidden bg-background border-b border-border">
        <div className="px-5 py-6">
          <p className="text-[12.5px] uppercase tracking-[0.14em] text-muted-foreground">Direkt starten</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["Neubau", "Modernisierung", "Wartung"].map((value) => (
              <Link
                key={value}
                to={`/wizard?anliegen=${value}`}
                className="inline-flex h-11 items-center rounded-full border border-border bg-secondary/60 px-4 text-[14.5px] font-medium text-foreground transition-colors hover:bg-secondary"
              >
                {value}
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* ══ VERTRAUENSSTREIFEN ══════════════════════ */}
      <section aria-label="Kennzahlen" className="relative overflow-hidden bg-background border-b border-border">
        <DecorHatch className="left-0 top-0 h-full w-[26%] sm:w-[18%]" fade="right" />
        <DecorHatch className="right-0 top-0 h-full w-[26%] sm:w-[18%]" fade="left" />
        <div className="relative max-w-5xl mx-auto px-6 py-7 sm:py-8">

          <Reveal>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-y-0 text-center sm:divide-x sm:divide-border">
              {[
                ["Persönlich geprüft", "jede Anfrage vor der Weiterleitung"],
                ["Direkter Kontakt", "ohne Zwischenhändler"],
                ["CHF 0.-", "Kosten für Kunden"],
              ].map(([value, label]) => (
                <div key={label} className="px-4">
                  <dt className="text-[22px] sm:text-[26px] font-semibold tracking-[-0.02em] text-foreground tabular-nums">
                    {value}
                  </dt>
                  <dd className="mt-1 text-[13.5px] text-muted-foreground leading-[1.6]">{label}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>



      {/* ══ PLATTFORM-DEFINITION ═══════════════════ */}
      <section id="ueber" aria-label="Was ist Liftblick.ch" className="relative overflow-hidden bg-background scroll-mt-16">
        <DecorShaft className="hidden sm:block right-[7%] top-0 bottom-0 w-[92px]" />
        <DecorFloors className="hidden lg:block left-[5%] top-[18%] bottom-[18%] w-[86px] opacity-60" count={7} />
        <div className="relative max-w-3xl px-6 py-14 sm:py-20 mx-auto">

          <Reveal>
            <Eyebrow>Was ist Liftblick.ch</Eyebrow>
            <h2 className="mt-5 text-[26px] sm:text-[32px] font-semibold tracking-[-0.025em] text-foreground leading-[1.15] text-balance">
              Schweizer Plattform für Liftanfragen.
            </h2>
            <p className="mt-6 text-[16.5px] sm:text-[17.5px] text-foreground/80 leading-[1.7] max-w-[62ch]">
              Liftblick.ch hilft Kunden in der Schweiz, passende Liftanbieter zu finden. Sie erfassen Ihr Liftprojekt online, registrierte Anbieter sehen passende Anfragen im geschützten Portal und melden sich direkt bei Ihnen.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <dl className="mt-9 grid gap-x-10 gap-y-6 sm:grid-cols-2 text-left border-t border-border pt-8">
              {[
                ["Schweizer Plattform", "Anfragen aus der ganzen Schweiz willkommen."],
                ["Kostenlos für Kunden", "Anfrage erfassen ist unverbindlich und gratis."],
                ["Direkter Kontakt", "Anbieter melden sich ohne Zwischenstelle."],
                ["Keine Zwischenhändler", "Preise und Technik klären Sie mit dem Anbieter."],
              ].map(([t, d]) => (
                <div key={t}>
                  <dt className="text-[14.5px] font-semibold text-foreground">{t}</dt>
                  <dd className="mt-1.5 text-[14.5px] text-muted-foreground leading-[1.65]">{d}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={140}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={goWizard} className={CTA_PRIMARY}>
                Liftanfrage kostenlos erfassen
              </Button>
              <Button size="lg" variant="outline" asChild className={CTA_BASE}>
                <Link to="/#so-funktioniert">So funktioniert es</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ WAS NACH DEM ABSENDEN PASSIERT ═════════ */}
      <section aria-label="Nach dem Absenden" className="relative overflow-hidden bg-[#F7F8FA] border-y border-border">
        <DecorHatch className="right-0 top-0 h-full w-[42%] sm:w-[30%] deco-hatch-dense opacity-70" fade="left" />
        <DecorDiagonal className="hidden sm:block left-[-6%] bottom-8 w-[42%]" />
        <div className="relative max-w-3xl mx-auto px-6 py-12 sm:py-14">

          <Reveal>
            <h2 className="text-[20px] sm:text-[23px] font-semibold tracking-[-0.02em] text-foreground">
              Was nach dem Absenden passiert.
            </h2>
            <ol className="mt-6 space-y-3 text-[15px] text-muted-foreground leading-[1.7] max-w-[62ch]">
              <li>Sie erhalten eine Bestätigung mit Referenznummer per E-Mail.</li>
              <li>Passende regionale Anbieter sehen Ihre Anfrage im geschützten Portal.</li>
              <li>Interessierte Anbieter melden sich direkt bei Ihnen.</li>
            </ol>
          </Reveal>
        </div>
      </section>

      {/* ══ THEMENEINSTIEGE ════════════════════════ */}
      <ThemenSection />

      {/* ══ SO FUNKTIONIERT ES (Anker, alle Geräte) ═ */}
      <SoFunktioniertSection />

      {/* ══ ONEPAGER SECTIONS (Desktop only) ═══════ */}
      <div className="hidden lg:block">
        <LiftartenSection />
        <FAQSection />

        {/* Abschluss-CTA vor der Kontaktsektion */}
        <section aria-label="Liftanfrage starten" className="relative overflow-hidden bg-background">
          <DecorShaft className="left-[9%] top-0 bottom-0 w-[74px] opacity-80" />
          <DecorShaft className="right-[9%] top-0 bottom-0 w-[74px] opacity-70" />
          <div className="relative max-w-3xl mx-auto px-6 py-16 sm:py-20 text-center">

            <h2 className="text-[26px] sm:text-[32px] font-semibold tracking-[-0.025em] text-foreground leading-[1.15] text-balance">
              Bereit für Ihre Liftanfrage?
            </h2>
            <p className="mt-5 text-[16.5px] text-muted-foreground leading-[1.7] max-w-xl mx-auto">
              In wenigen Minuten erfasst. Kostenlos, unverbindlich und direkt an passende Schweizer Anbieter.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={goWizard} className={CTA_PRIMARY}>
                Anfrage starten
              </Button>
              <Button size="lg" variant="outline" asChild className={CTA_BASE}>
                <Link to="/fuer-anbieter">Ich bin Liftanbieter</Link>
              </Button>
            </div>
          </div>
        </section>

        <KontaktSection />
      </div>

    </SiteLayout>
  );
};

export default Landing;
